import { Component, EventEmitter, Input, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { finalize } from 'rxjs';
import { PreLaunchEvmBalanceDetail } from '../../../../../models/task.model';
import { WalletOwnerTypeEnum } from '../../../../../models/wallet.model';
import { FormatCoinCurrencyPipe } from '@olv/ui-shared/pipes/format-coin-currency.pipe';
import { TaskService } from '@olv/ui-shared/services/task.service';
import { ButtonComponent, ButtonState, toButtonState } from '@olv/ui-shared/ui-shared/button/button.component';
import { StateBadgeComponent } from '@olv/ui-shared/ui-shared/state-badge/state-badge.component';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { shortenAddress } from '@olv/ui-shared/utils/shortenAddress.utils';
import { getTransactionExplorerUrl } from '../../../../../utils/explorer-url.util';

type CopyField = 'wallet' | 'hash' | 'transaction';

@Component({
  selector: 'app-pre-launch-evm-detail',
  standalone: true,
  imports: [NgTemplateOutlet, FormsModule, MatIconModule, MatTooltipModule, StateBadgeComponent, ButtonComponent, FormatCoinCurrencyPipe],
  templateUrl: './pre-launch-evm-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pre-launch-evm-detail.component.scss'],
})
export class PreLaunchEvmDetailComponent {
  @Input() detail?: PreLaunchEvmBalanceDetail;
  @Input() description?: string;
  @Input() preparedBy?: string;
  @Input() createdDate?: Date;
  @Input() isHistory = false;
  @Input() resolvedBy?: string;
  @Input() resolvedAt?: Date;
  @Input() status?: string;
  @Input() comment?: string;
  @Input() taskId?: string;
  @Input() relatedTransactionId?: string;

  @Output() refresh = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  protected readonly shortenAddress = shortenAddress;
  protected readonly formatDateStandard = formatDateStandard;
  copiedField?: CopyField;
  commentText = '';
  isProcessing = false;
  errorMessage = '';

  private taskService = inject(TaskService);
  private toastr = inject(ToastService);
  private router = inject(Router);

  get displayDescription(): string {
    const description = this.description ?? '';
    const markerStart = description.lastIndexOf('[prelaunch-evm');
    return (markerStart >= 0 ? description.slice(0, markerStart) : description).trim();
  }

  get explorerUrl(): string | null {
    return getTransactionExplorerUrl(this.detail?.networkName, this.detail?.transactionHash);
  }

  get canOpenTransaction(): boolean {
    return !!this.relatedTransactionId && !!this.detail?.twinWalletId && this.isWalletOwnerType(this.detail?.walletOwnerType);
  }

  get processingButtonState(): ButtonState {
    return toButtonState(this.isProcessing, false);
  }

  onAcknowledge(): void {
    if (!this.taskId || this.isHistory) return;

    this.isProcessing = true;
    this.errorMessage = '';

    this.taskService
      .acknowledgeTask(this.taskId, { comment: this.commentText.trim() || undefined })
      .pipe(finalize(() => (this.isProcessing = false)))
      .subscribe({
        next: () => {
          this.refresh.emit();
          this.toastr.success('Task acknowledged successfully');
          this.closed.emit();
        },
        error: (error) => {
          this.errorMessage = error.error?.message || 'Failed to acknowledge task';
          this.toastr.error(this.errorMessage);
        },
      });
  }

  openTransaction(): void {
    const walletId = this.detail?.twinWalletId;
    const ownerType = this.detail?.walletOwnerType;
    if (!this.relatedTransactionId || !walletId || !this.isWalletOwnerType(ownerType)) return;

    this.router.navigate(['/wallet-detail', walletId], {
      queryParams: {
        type: ownerType === WalletOwnerTypeEnum.Merchant ? 'merchant' : 'system',
        search: this.relatedTransactionId,
        returnUrl: this.isHistory ? '/tasks/history' : '/tasks',
        backLabel: this.isHistory ? 'Task History' : 'Tasks',
      },
    });
    this.closed.emit();
  }

  copy(value: string, field: CopyField, tooltip: MatTooltip): void {
    navigator.clipboard.writeText(value).then(() => {
      this.copiedField = field;
      tooltip.show();
      setTimeout(() => {
        this.copiedField = undefined;
      }, 1500);
    });
  }

  private isWalletOwnerType(value: WalletOwnerTypeEnum | null | undefined): value is WalletOwnerTypeEnum {
    return value === WalletOwnerTypeEnum.Merchant || value === WalletOwnerTypeEnum.System;
  }
}
